import { Injectable, inject } from '@angular/core';

import { SportsCacheService } from '@frontend/sports/betting-offer/feature/sports-cache';
import { GeolocationConfig } from '@frontend/sports/common/client-config-data-access';
import { CountItem } from '@frontend/sports/common/core/data-access/sport-model';
import { CompetitionIdService } from '@frontend/sports/competition/feature';
import { WidgetContext, WidgetPage } from '@frontend/sports/types/components/widget';
import { GeolocationService } from '@frontend/vanilla/core';
import { Observable, first, forkJoin, map, of } from 'rxjs';

export interface WidgetContextEnhancement {
    location?: string;
    realCompetitionId?: number;
    compoundCompetitionId?: string;
}

interface EnhancedCompetitionId {
    realCompetitionId?: number;
    compoundCompetitionId?: string;
}

@Injectable({ providedIn: 'root' })
export class WidgetContextEnhanceService {
    private geolocation = inject(GeolocationService);
    private geolocationConfig = inject(GeolocationConfig);
    private bettingCache = inject(SportsCacheService);
    private competitionIdService = inject(CompetitionIdService);

    getEnhancedProperties(context: WidgetContext): Observable<WidgetContextEnhancement> {
        return forkJoin([this.getCompetitionIds$(context), this.getGeolocation$()]).pipe(
            map(([competitionIds, location]) => ({
                ...competitionIds,
                location,
            })),
        );
    }

    private getCompetitionIds$(context: WidgetContext): Observable<EnhancedCompetitionId | undefined> {
        if (context.page !== WidgetPage.CompetitionLobby) {
            return of(undefined);
        }

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return this.bettingCache.getSport(context.sportId!).pipe(
            map((sport) =>
                sport
                    ? {
                          realCompetitionId: this.getRealVirtualCompetitionId(sport, context),
                          compoundCompetitionId: this.getCompoundCompetitionId(sport, context),
                      }
                    : undefined,
            ),
        );
    }

    private getRealVirtualCompetitionId(sport: CountItem, context: WidgetContext): number | undefined {
        if (!context.virtualCompetitionId) {
            return undefined;
        }

        return this.competitionIdService.getRealVirtualCompetitionId(
            sport,
            context.regionId || context.tournamentId,
            context.virtualCompetitionId,
            context.virtualCompetitionGroupId,
        );
    }

    private getCompoundCompetitionId(sport: CountItem, context: WidgetContext): string | undefined {
        if (context.virtualCompetitionId) {
            return undefined;
        }

        return this.competitionIdService.getCompoundId(sport, context.competitionId);
    }

    private getGeolocation$(): Observable<string | undefined> {
        if (!this.geolocationConfig.isGeolocationEnabled) {
            return of(undefined);
        }

        return this.geolocation.whenReady.pipe(
            first(),
            map(() => this.geolocation.currentPosition?.mappedLocation?.locationId || undefined),
        );
    }
}
